/**
 * Utility functions for handling YouTube URLs and metadata
 */

/**
 * Extracts YouTube video ID from various URL formats
 * @param url - YouTube URL in any format
 * @returns Video ID string or empty string if not found
 */
export const extractYouTubeVideoId = (url: string): string => {
  try {
    if (!url) return '';

    // Regular YouTube URL: https://www.youtube.com/watch?v=VIDEO_ID
    if (url.includes('youtube.com/watch')) {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('v') || '';
    }

    // Short YouTube URL: https://youtu.be/VIDEO_ID
    if (url.includes('youtu.be/')) {
      const parts = url.split('youtu.be/');
      if (parts.length > 1) {
        return parts[1].split('?')[0].split('&')[0];
      }
    }

    // YouTube embed URL: https://www.youtube.com/embed/VIDEO_ID
    if (url.includes('youtube.com/embed/')) {
      const parts = url.split('youtube.com/embed/');
      if (parts.length > 1) {
        return parts[1].split('?')[0].split('&')[0];
      }
    }

    return '';
  } catch (error) {
    console.error('Error extracting YouTube video ID:', error);
    return '';
  }
};

/**
 * Generates YouTube thumbnail URL from video ID
 * @param videoId - YouTube video ID
 * @param quality - Thumbnail quality ('maxres' | 'hq' | 'mq' | 'sd' | 'default')
 * @returns Thumbnail URL
 */
export const getYouTubeThumbnailUrl = (
  videoId: string,
  quality: 'maxres' | 'hq' | 'mq' | 'sd' | 'default' = 'maxres'
): string => {
  if (!videoId) return '';

  const qualityMap = {
    maxres: 'maxresdefault',
    hq: 'hqdefault',
    mq: 'mqdefault',
    sd: 'sddefault',
    default: 'default'
  };

  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

/**
 * Gets YouTube thumbnail URL directly from video URL
 * @param url - YouTube video URL
 * @param quality - Thumbnail quality
 * @returns Thumbnail URL or empty string if not a valid YouTube URL
 */
export const getYouTubeThumbnail = (
  url: string,
  quality: 'maxres' | 'hq' | 'mq' | 'sd' | 'default' = 'maxres'
): string => {
  const videoId = extractYouTubeVideoId(url);
  return videoId ? getYouTubeThumbnailUrl(videoId, quality) : '';
};

/**
 * Cleans and normalizes YouTube URL to standard format
 * @param url - YouTube URL in any format
 * @returns Cleaned YouTube URL
 */
export const cleanYouTubeUrl = (url: string): string => {
  const videoId = extractYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/watch?v=${videoId}` : url;
};

/**
 * Validates if a URL is a YouTube URL
 * @param url - URL to validate
 * @returns True if it's a valid YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('youtube.com') || url.includes('youtu.be');
};
